import React, { useEffect, useState } from "react";
import { api, formatRM } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, SlidersHorizontal } from "lucide-react";
import { TierPrices, TIERS, TIER_LABEL } from "@/components/ProductVariants";
import { ProductImage, ProductImageUpload } from "@/components/ProductImage";
import { SpecsEditor, FinishingEditor, PricingEditor, RulesEditor } from "@/components/W2PEditors";

const EMPTY = { name: "", category: "", unit: "pcs", base_price: 0, agent_price: 0, description: "", active: true, tier_prices: {}, specs: [], finishing: [], pricing: { mode: "unit" }, rules: [], weight_per_unit: "" };
const cleanPrices = (p) => Object.fromEntries(Object.entries(p || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([k, v]) => [k, Number(v)]));
const fmtAdd = (v) => (Number(v) ? `${Number(v) > 0 ? "+" : "−"}RM${Math.abs(Number(v)).toFixed(2)}` : "");

const Step = ({ n, title, hint, children }) => (
  <section className="space-y-2">
    <div className="flex items-baseline gap-2">
      <span className="w-5 h-5 rounded-full bg-cyan-600 text-white text-[11px] font-bold flex items-center justify-center">{n}</span>
      <h3 className="font-semibold text-sm">{title}</h3>
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </div>
    <div className="pl-7 space-y-3">{children}</div>
  </section>
);

export const ProductCatalog = ({ isAdmin }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (p) => setEditing({ ...EMPTY, ...p, specs: (p.specs || []).map((g) => ({ ...g, choices: (g.choices || []).map((c) => ({ ...c })) })), finishing: (p.finishing || []).map((g) => ({ ...g, choices: (g.choices || []).map((c) => ({ ...c })) })), pricing: p.pricing || { mode: "unit" }, rules: p.rules || [], tier_prices: p.tier_prices || {}, weight_per_unit: p.weight_per_unit ?? "" });
  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const tier_prices = cleanPrices(editing.tier_prices);
    const payload = {
      name: editing.name, category: editing.category, unit: editing.unit, description: editing.description, active: editing.active,
      base_price: Number(editing.base_price) || 0, agent_price: Number(tier_prices.normal ?? editing.agent_price ?? 0), tier_prices,
      specs: editing.specs.filter((g) => g.label.trim()).map((g) => ({ ...g, label: g.label.trim(), required: g.required !== false, choices: (g.choices || []).filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), add_on: Number(c.add_on) || 0, add_on_type: c.add_on_type || "per_unit" })) })),
      finishing: editing.finishing.filter((g) => g.label.trim()).map((g) => ({ ...g, label: g.label.trim(), required: g.required === true, choices: (g.choices || []).filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), add_on: Number(c.add_on) || 0, add_on_type: c.add_on_type || "per_unit" })) })),
      pricing: { ...editing.pricing, setup_fee: Number(editing.pricing.setup_fee) || 0, min_qty: Number(editing.pricing.min_qty) || 0, min_area: Number(editing.pricing.min_area) || 0, area_price: cleanPrices(editing.pricing.area_price), qty_breaks: (editing.pricing.qty_breaks || []).filter((b) => b.min_qty !== "").map((b) => ({ min_qty: Number(b.min_qty) || 0, prices: cleanPrices(b.prices) })) },
      rules: editing.rules.filter((r) => r.if?.group && r.if?.choice),
      weight_per_unit: Number(editing.weight_per_unit) || 0,
      options: editing.specs.filter((g) => g.type !== "number" && g.label.trim()).map((g) => ({ label: g.label.trim(), required: g.required !== false, choices: (g.choices || []).filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), add_on: Number(c.add_on) || 0 })) })),
      details: [], variants: [], variant_fields: [],
    };
    try {
      if (editing.id) { await api.patch(`/products/${editing.id}`, payload); toast.success("Product saved"); setEditing(null); }
      else {
        const { data } = await api.post("/products", payload);
        toast.success("Product created — you can now upload a sample photo");
        setEditing({ ...editing, id: data.id, created_at: data.created_at });
      }
      load();
    } catch { toast.error("Failed to save product"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`); load();
  };
  const toggle = async (p) => { await api.patch(`/products/${p.id}`, { active: !p.active }); load(); };

  return (
    <div className="space-y-3" data-testid="product-catalog">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-display font-bold flex items-center gap-2"><Package className="w-5 h-5" /> Product Catalog</h2>
          <p className="text-sm text-muted-foreground">{items.length} products • options change the final price • agent tier prices used in the Agent Portal</p>
        </div>
        {isAdmin && <Button onClick={openNew} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" /> Add Product</Button>}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {items.map((p) => (
          <Card key={p.id} className={`p-4 space-y-2 ${!p.active ? "opacity-60" : ""}`} data-testid={`product-row-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-3 min-w-0">
                <ProductImage product={p} className="w-16 h-16 shrink-0" testid={`product-image-${p.id}`} />
                <div>
                  <div className="font-display font-bold">{p.name} {!p.active && <Badge variant="secondary">Inactive</Badge>}</div>
                  <div className="text-xs text-muted-foreground">{p.category || "—"} • per {p.unit}</div>
                </div>
              </div>
              <div className="text-right text-sm font-mono">
                <div>Retail {formatRM(p.base_price)}</div>
                <div className="text-amber-600 font-bold">Agent {formatRM(p.tier_prices?.normal ?? p.agent_price)}</div>
              </div>
            </div>
            {p.tier_prices && Object.keys(p.tier_prices).length > 1 && (
              <div className="flex flex-wrap gap-1 text-[11px] font-mono">
                {TIERS.filter((t) => p.tier_prices[t] !== undefined).map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-muted">{TIER_LABEL[t]} {formatRM(p.tier_prices[t])}</span>)}
              </div>
            )}
            {[...(p.specs || []), ...(p.finishing || [])].length > 0 && (
              <div className="text-xs space-y-0.5" data-testid={`product-options-${p.id}`}>
                {p.pricing?.mode === "area" && <div className="text-amber-700 font-semibold">Area pricing (per sqft){p.pricing.setup_fee ? ` • setup RM${p.pricing.setup_fee}` : ""}</div>}
                {p.pricing?.qty_breaks?.length > 0 && <div className="text-amber-700 font-semibold">{p.pricing.qty_breaks.length} quantity breaks{p.pricing.setup_fee ? ` • setup RM${p.pricing.setup_fee}` : ""}</div>}
                {[...(p.specs || []), ...(p.finishing || [])].map((g) => g.type === "number" ? <div key={g.label}><span className="font-semibold">{g.label}:</span> <span className="px-1.5 py-0.5 rounded bg-muted">number ({g.unit || "ft"}){g.role ? ` • ${g.role}` : ""}</span></div> : (
                  <div key={g.label} className="flex gap-1 flex-wrap items-center">
                    <span className="font-semibold">{g.label}:</span>
                    {(g.choices || []).map((c) => <span key={c.name} className="px-1.5 py-0.5 rounded bg-muted">{c.name}{Number(c.add_on) ? <span className="text-amber-600 font-mono"> {c.add_on_type === "percent" ? `+${Number(c.add_on)}%` : fmtAdd(c.add_on)}{c.add_on_type === "fixed" ? " once" : c.add_on_type === "per_sqft" ? "/sqft" : ""}</span> : null}</span>)}
                  </div>
                ))}
              </div>
            )}
            {isAdmin && (
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => toggle(p)}>{p.active ? "Deactivate" : "Activate"}</Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(p)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-6" data-testid="product-form">
              <Step n={1} title="Product" hint="name, photo & unit">
                <ProductImageUpload product={editing} onChanged={(img) => { set("image", img); load(); }} />
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Product Name *</Label><Input required value={editing.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Business Card" data-testid="product-name" /></div>
                  <div><Label>Category</Label><Input value={editing.category} onChange={(e) => set("category", e.target.value)} placeholder="Digital Print / Large Format / Signage" /></div>
                  <div><Label>Sold per</Label><Input value={editing.unit} onChange={(e) => set("unit", e.target.value)} placeholder="pcs / 100pcs / sqft" /></div>
                  <div className="col-span-2"><Label>Short description</Label><Textarea rows={2} value={editing.description} onChange={(e) => set("description", e.target.value)} placeholder="What the customer gets" /></div>
                </div>
              </Step>

              <Step n={2} title="Specifications" hint="sizes, materials, colours, numeric fields">
                <SpecsEditor specs={editing.specs} setSpecs={(v) => set("specs", v)} areaMode={editing.pricing?.mode === "area"} />
              </Step>
              <Step n={3} title="Finishing" hint="lamination, spot UV, foil, corners, punching + charges">
                <FinishingEditor finishing={editing.finishing} setFinishing={(v) => set("finishing", v)} />
              </Step>
              <Step n={4} title="Pricing" hint="quantity breaks, setup fee, per-sqft, weight">
                <PricingEditor pricing={editing.pricing} setPricing={(v) => set("pricing", v)} weight={editing.weight_per_unit} setWeight={(v) => set("weight_per_unit", v)} />
              </Step>
              <Step n={5} title="Compatibility rules" hint="required combos, disallowed combos, minimum qty">
                <RulesEditor rules={editing.rules} setRules={(v) => set("rules", v)} specs={editing.specs} finishing={editing.finishing} />
              </Step>

              <Step n={6} title="Base Price" hint="fallback per-piece price when no qty break matches">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Retail Price (RM)</Label><Input type="number" step="0.01" min="0" value={editing.base_price} onChange={(e) => set("base_price", e.target.value)} className="font-mono" data-testid="product-base-price" /></div>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><SlidersHorizontal className="w-3.5 h-3.5" /> Agent Price by Tier (RM)</Label>
                  <p className="text-[11px] text-muted-foreground mb-1">Leave Silver / Gold / Diamond empty to use the Normal price. Option add-ons apply on top for every tier.</p>
                  <TierPrices value={{ ...editing.tier_prices, normal: editing.tier_prices?.normal ?? editing.agent_price }} onChange={(p) => set("tier_prices", p)} testidPrefix="product-tier" />
                </div>
              </Step>

              <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="product-save">{editing.id ? "Save Changes" : "Create Product"}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
